/**
 * Lender-domain formatters. INR-aware, grain-aware.
 *
 * Voice rule: speak the way lenders speak.
 *   - "Disbursement amount", not "loan revenue"
 *   - "Outstanding", not "balance"
 *   - "DPD" stays DPD (everyone in collections knows it)
 *   - "Bucket 31–60", not "Stage 2"
 */

const INR_COMPACT = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  notation: 'compact',
  maximumFractionDigits: 1,
})

const INR_FULL = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
})

const INR_PRECISE = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
})

const INT_COMPACT = new Intl.NumberFormat('en-IN', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

const INT_FULL = new Intl.NumberFormat('en-IN')

/** "₹1.25Cr", "₹42L", "₹8,500". Compact for ≥ 1 lakh. */
export function inr(n: string | number | null | undefined): string {
  if (n == null) return '—'
  const v = typeof n === 'string' ? Number(n) : n
  if (!Number.isFinite(v)) return '—'
  if (Math.abs(v) >= 100_000) return INR_COMPACT.format(v)
  return INR_FULL.format(v)
}

/** Always full-precision INR. Use on detail pages where every paisa matters. */
export function inrFull(n: string | number | null | undefined): string {
  if (n == null) return '—'
  const v = typeof n === 'string' ? Number(n) : n
  if (!Number.isFinite(v)) return '—'
  return INR_PRECISE.format(v)
}

/** "1.2M", "42K", "8,500" — for non-money counters. */
export function num(n: number | null | undefined): string {
  if (n == null) return '—'
  if (!Number.isFinite(n)) return '—'
  if (Math.abs(n) >= 10_000) return INT_COMPACT.format(n)
  return INT_FULL.format(n)
}

/** "3", "12", "—" — DPD never abbreviates. */
export function dpd(n: number | null | undefined): string {
  if (n == null) return '—'
  return n.toString()
}

/** Bucket display: "BUCKET_31_60" → "31–60", "BUCKET_90_PLUS" → "90+", "CURRENT" → "0". */
export function bucketLabel(b: string | null | undefined): string {
  if (!b) return '—'
  if (b === 'CURRENT') return '0'
  if (b === 'WRITE_OFF') return 'Write-off'
  return b.replace('BUCKET_', '').replace(/_PLUS$/, '+').replace('_', '–')
}

/** Filter chip for the CURRENT (0 DPD) bucket, which the business calls the regular bucket. */
export const REGULAR_BUCKET_LABEL = 'Regular (0)'

/**
 * The ageing column a stored bucket belongs to. NPA and WRITE_OFF are both
 * past 90 days, so they count as 90+; anything else is its own column.
 */
export function ageingBucket(b: string): string {
  return b === 'NPA' || b === 'WRITE_OFF' ? 'BUCKET_90_PLUS' : b
}

/** "2m ago", "3h ago", "Jun 4", "Mar 2024". Grain-aware. */
export function relativeTime(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return '—'
  const diff = now - t
  if (diff < 0) return 'in the future'
  const s = Math.floor(diff / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 14) return `${d}d ago`
  return new Date(t).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

/** "04 Jun 2026 14:32" — operational timestamp. */
export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return '—'
  return new Date(t).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
}

/** "04 Jun 2026". */
export function date(iso: string | null | undefined): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return '—'
  return new Date(t).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

/** Bucket → chart color slot (chart-1 .. chart-8). Stable. */
export function bucketColor(b: string | null | undefined): string {
  switch (b) {
    case 'CURRENT':       return 'var(--tw-color-chart-5, #14855B)' // teal — positive
    case 'BUCKET_1_30':   return '#4A77BD'                          // navy-500
    case 'BUCKET_31_60':  return '#B57B0B'                          // gold — caution
    case 'BUCKET_61_90':  return '#9D2C7A'                          // plum — escalating
    case 'BUCKET_90_PLUS':return '#C0382B'                          // red  — NEGATIVE
    default:              return '#A3BDEA'                          // navy-300 — unknown
  }
}

export function priorityColor(p: string | null | undefined): { fg: string; bg: string } {
  switch (p) {
    case 'P1': return { fg: '#7A1A12', bg: '#FCE7E2' } // red soft
    case 'P2': return { fg: '#7A5304', bg: '#FBF1D5' } // gold soft
    case 'P3': return { fg: '#16336B', bg: '#E5EFFF' } // navy soft
    case 'P4': return { fg: '#475569', bg: '#F1F5F9' } // slate
    default:   return { fg: '#475569', bg: '#F1F5F9' }
  }
}

/**
 * The calendar date as the user's own clock reads it, `YYYY-MM-DD`.
 *
 * `toISOString().slice(0, 10)` looks like it does this and does not: it yields
 * the **UTC** date. In IST that is yesterday's date between 00:00 and 05:30, so
 * a collection taken at 01:45 fell outside a range whose upper bound was
 * "today" and disappeared from the list. The variant that builds local midnight
 * first — `new Date(y, m, d).toISOString()` — is worse: it converts back to UTC
 * and reads yesterday at *every* hour of an IST day.
 *
 * Date filters are calendar dates, not instants. Build the string from the
 * local parts and no conversion can happen.
 */
export function localDate(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** [localDate] shifted by whole days — negative for the past. */
export function localDateOffset(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return localDate(d)
}

/**
 * Money as an agent types it → a string the API can bind to a `BigDecimal`.
 *
 * A telecaller types what the borrower just said, and in India that is
 * "1,000". Jackson has no notion of grouping separators, so an unsanitised
 * field surfaced as `JSON parse error: Cannot deserialize value of type
 * java.math.BigDecimal from String "1,000"` — a 500-shaped error for a
 * perfectly ordinary keystroke, mid-call.
 *
 * Only `,` is grouping. `.` is always the decimal point, so "1.000" is one
 * rupee and not a thousand. Reading it the other way is how a promise for
 * ₹1,000 gets recorded as ₹1 — and the guess would be invisible, because both
 * readings look like a valid amount.
 *
 * Returns null for anything that is not a positive amount, so the caller can
 * refuse rather than send a figure it had to invent.
 */
export function parseAmount(raw: string | number | null | undefined): string | null {
  if (raw == null) return null
  // Numbers reach here too: the API's BigDecimal fields serialise as JSON
  // numbers however the TS types describe them, so a quick-fill button hands
  // this a number while the keyboard hands it a string.
  const cleaned = String(raw).replace(/[\s,₹]/g, '')
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null
  if (Number(cleaned) <= 0) return null
  return cleaned
}

/**
 * A code the label map does not know, made readable.
 *
 * "MICRO_ENTERPRISE" → "Micro enterprise". A single all-caps word is left
 * alone — MSME, POS and UPI are acronyms, and "Msme" is worse than the code.
 * Anything already in mixed case is the lender's own wording ("Home Loan")
 * and is shown exactly as they wrote it.
 */
export function humanizeCode(code: string | null | undefined): string {
  if (!code) return '—'
  const trimmed = code.trim()
  if (!trimmed) return '—'
  if (/[a-z]/.test(trimmed)) return trimmed
  if (!/[_\s-]/.test(trimmed)) return trimmed
  const words = trimmed.replace(/[_\s-]+/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Seconds as a ticking clock: "14:59", "0:05", and "1:00:00" from an hour up.
 * Anything that is not a positive number reads "0:00", so a countdown that
 * has run out — or was never valid — shows as finished rather than odd.
 */
export function countdownLabel(totalSeconds: number): string {
  const s = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0
  const pad = (n: number) => String(n).padStart(2, '0')
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`
}
